<div class="cx-scope cx-dashboard">
  <div class="cx-header cx-rise">
    <div class="cx-header-text">
      <h1>{{ dashboard?.tenantName || 'Connect' }}</h1>
      <p>Receivables and collections at a glance</p>
    </div>
  </div>

  <div class="cx-problem" role="alert" *ngIf="problem">
    <cx-icon name="circle-alert" class="cx-problem-icon"></cx-icon>
    <div class="cx-problem-text">
      <p class="cx-problem-heading">{{
        problem === 'not-linked' ? 'Your account is not linked to MedicAI Connect'
        : problem === 'off' ? 'This practice is not on MedicAI Connect'
        : 'MedicAI Connect could not be reached' }}</p>
      <p class="cx-problem-message">{{
        problem === 'not-linked' ? 'Ask your practice admin to add you to MedicAI Connect, then open this page again.'
        : problem === 'off' ? 'Connect is switched on per practice in Settings, under Subscription.'
        : 'Check your connection and try again.' }}</p>
    </div>
    <button type="button" class="cx-btn cx-btn--outline cx-btn--default-size" *ngIf="problem === 'failed'" (click)="load()">Try again</button>
  </div>

  <div class="cx-tiles cx-stagger" *ngIf="stats as s">
    <ng-container *ngIf="s.billedCents !== null">
      <ng-container *ngTemplateOutlet="tile; context: { label: 'Billed in ' + s.month, value: statRand(s.billedCents), sub: 'Claims and invoices raised', icon: 'file-text', danger: false }"></ng-container>
      <ng-container *ngTemplateOutlet="tile; context: { label: 'Received in ' + s.month, value: statRand(s.receivedCents ?? 0), sub: 'Schemes ' + rand(s.schemeReceivedCents ?? 0) + ' · Patients ' + rand(s.patientReceivedCents ?? 0), icon: 'banknote', danger: false }"></ng-container>
    </ng-container>
    <ng-container *ngTemplateOutlet="tile; context: { label: 'Waiting on schemes', value: statRand(s.schemeDueCents), sub: countText(s.schemeDocuments, 'claim') + ' with money at a scheme', icon: 'landmark', danger: false }"></ng-container>
    <ng-container *ngTemplateOutlet="tile; context: { label: 'Patients owe', value: statRand(s.patientDueCents), sub: countText(s.patientDocuments, 'document') + ' with patient money open', icon: 'wallet', danger: false }"></ng-container>
    <ng-container *ngTemplateOutlet="tile; context: { label: 'Outstanding', value: statRand(s.outstandingCents), sub: countText(s.openDocuments, 'open document') + ' · ' + countText(s.patients, 'patient'), icon: 'users', danger: false }"></ng-container>
    <ng-container *ngTemplateOutlet="tile; context: { label: 'Needs attention', value: statInt(s.attention), sub: 'Rejected, unsent or short-paid claims', icon: 'octagon-alert', danger: s.attention > 0 }"></ng-container>
    <ng-container *ngTemplateOutlet="tile; context: { label: 'Aging 90+ days', value: statInt(s.overdue), sub: 'Open documents 90+ days after service', icon: 'clock', danger: s.overdue > 0 }"></ng-container>
    <ng-container *ngTemplateOutlet="tile; context: { label: 'New submissions', value: statInt(s.incoming), sub: 'Awaiting submission to the scheme', icon: 'inbox', danger: false }"></ng-container>
  </div>

  <ng-template #tile let-label="label" let-value="value" let-sub="sub" let-icon="icon" let-danger="danger">
    <section class="cx-tile">
      <span class="cx-tile-label">{{ label }}</span>
      <span class="cx-tile-value">{{ value }}</span>
      <span class="cx-tile-sub">{{ sub }}</span>
      <span class="cx-tile-icon" [class.cx-tile-icon--danger]="danger" aria-hidden="true">
        <cx-icon [name]="icon"></cx-icon>
      </span>
    </section>
  </ng-template>

  <section class="cx-board" *ngIf="loading || dashboard">
    <div class="cx-skeleton-list" role="status" aria-live="polite" *ngIf="loading">
      <span class="cx-sr-only">Loading…</span>
      <div class="cx-skeleton-card" aria-hidden="true" *ngFor="let row of [1, 2, 3, 4]">
        <div class="cx-skeleton cx-skeleton--a"></div>
        <div class="cx-skeleton cx-skeleton--b"></div>
        <div class="cx-skeleton cx-skeleton--c"></div>
      </div>
    </div>

    <div class="cx-buckets cx-stagger" *ngIf="!loading && dashboard as d">
      <div class="cx-bucket cx-bucket--requests" *ngIf="d.requests?.length">
        <h2>Requests from MedicAI Connect <span class="cx-badge">{{ d.requests!.length }}</span></h2>
        <div class="cx-row">
          <div class="cx-card-host" *ngFor="let r of d.requests">
            <div class="cx-aging cx-tone-info cx-request-card" (click)="openRequest(r)">
              <div class="cx-card-top">
                <span class="cx-aging-name">{{ r.patient.name }}</span>
                <span class="cx-chip cx-chip--upper cx-pill-info">Requested</span>
              </div>
              <span class="cx-meta cx-request-title">{{ requestTitle(r) }}</span>
              <span class="cx-meta" *ngIf="r.details.claimNumber">Claim {{ r.details.claimNumber }}</span>
              <span class="cx-meta cx-request-reason" *ngIf="r.details.reason">{{ r.details.reason }}</span>
              <div class="cx-actions">
                <button type="button" class="cx-btn cx-btn--info-line" (click)="openRequest(r); $event.stopPropagation()">
                  <cx-icon name="file-text"></cx-icon> Open request
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="cx-bucket cx-bucket--incoming">
        <h2>Presubmissions from MedicAI Clinical <span class="cx-badge">{{ d.buckets.incoming.length }}</span></h2>
        <div class="cx-row">
          <cx-claim-card *ngFor="let claim of d.buckets.incoming; trackBy: trackClaim" [claim]="claim"
            (open)="openClaim(claim)" (openPatientFile)="openPatientFile(claim)"
            (linkDetails)="openPatientFile(claim, true)"></cx-claim-card>
          <p class="cx-empty" *ngIf="!d.buckets.incoming.length">Nothing waiting. New submissions from MedicAI clinical appear here.</p>
        </div>
      </div>

      <h2 class="cx-section-title">Open claims and invoices</h2>

      <div class="cx-bucket" *ngFor="let bucket of buckets">
        <h3>{{ bucket.heading }} <span class="cx-badge">{{ items(bucket.key).length }}</span></h3>
        <div class="cx-row">
          <cx-aging-card *ngFor="let p of items(bucket.key); trackBy: trackAging" [item]="p" [chatGroupId]="chatGroupId"
            [request]="requestOf(p)" (openRequest)="openRequest($event)"
            (openAccount)="openAccount(p)" (openPatientFile)="openPatientFile(p)"></cx-aging-card>
          <p class="cx-empty" *ngIf="!items(bucket.key).length">{{ bucket.empty }}</p>
        </div>
      </div>
    </div>
  </section>
</div>
